import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  addToast,
} from "@heroui/react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { api } from "@/lib/api-client";
import type { PostWaitlistList } from "@/server-types";

type Entry = PostWaitlistList["response"]["entries"][number];

export function BookGroupConfirmModal({
  entry,
  isOpen,
  onClose,
  onSuccess,
}: {
  entry: Entry;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const { mutateAsync, isPending, processedErrorMessage } =
    api.waitlist.usePostAcceptGroup();

  const handleConfirm = async () => {
    try {
      await mutateAsync({ waitlistId: entry.waitlistId });
      addToast({ title: t`Termin rezerviran`, color: "success" });
      onSuccess?.();
      onClose();
    } catch (err) {
      Sentry.addBreadcrumb({
        category: "ui.action",
        message: "book group confirm error",
        level: "error",
        data: { error: err instanceof Error ? err.message : String(err) },
      });
      // Inline LimeAlert below surfaces the server-localized reason.
    }
  };

  const customerFullName =
    `${entry.customer.name ?? ""} ${entry.customer.lastName ?? ""}`.trim() ||
    t`Neznana stranka`;

  return (
    <LimeModal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <ModalContent>
        <ModalHeader>{t`Rezerviraj skupinski termin`}</ModalHeader>
        <ModalBody>
          <p className="text-sm">
            {t`Ali želite rezervirati termin za stranko:`}{" "}
            <strong>{customerFullName}</strong>?
          </p>
          <p className="text-default-500 text-xs">
            {t`Potrjujete rezervacijo mesta v skupinskem terminu. Po uspešni rezervaciji bo stranka dobila potrditveno obvestilo.`}
          </p>
          <LimeAlert message={processedErrorMessage} />
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={isPending}>
            {t`Prekliči`}
          </Button>
          <Button color="primary" onPress={handleConfirm} isLoading={isPending}>
            {t`Potrdi rezervacijo`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
}
